import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch } from "react";
import type { DsvBeleg, DsvEreignis, Order, SendungsTreffer, SendungsVorschlag, Zeitstrahl } from "../../lib/types.ts";
import type { Aktion } from "../../state/reduzierer.ts";
import { KonfliktFehler } from "../../api/basis.ts";
import { bestellungLaden, SSE_ENTITAET as BESTELLUNG_ENTITAET } from "../../api/bestellungen.ts";
import {
  dsvBelegeLaden, dsvHistorieLaden, sendungVerknuepfen, sendungZuBestellungLaden,
  sendungenSuchen, sendungsVorschlagLaden, verknuepfungLoesen, zeitstrahlLaden,
} from "../../api/sendungen.ts";
import { einbuchungenLaden, type EinbuchungenJePosition } from "../../api/einbuchungen.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import { mitBestellbezug } from "../../lib/bestellkennung.ts";
import { useMontiertRef } from "../../state/useMontiertRef.ts";

/** Bestellungsbezogene Spedition und Anlieferung; der globale Zeitstrahl bleibt beim Aufrufer. */
export function useDrawerSpedition(
  drawer: { id: string } | null,
  bestellungen: readonly Order[],
  versende: Dispatch<Aktion>,
  zeitstrahlListeNeuLaden: () => void,
  NACHLADEN_FEHLGESCHLAGEN_MELDUNG: string,
) {
  // ================= Reiter "Spedition": verknuepfte Sendung + Zeitstrahl (Auftrag D) ============
  //
  // Gehoeren zur GEOEFFNETEN Bestellung, nicht zum Gesamtbestand (Befund im Entscheidungsdokument
  // `docs/superpowers/decisions/2026-08-20-reiter-spedition.md`) - deshalb LOKALER `useState` dieser
  // Komponente statt eines Store-Feldes. `Speicherzustand` fuehrt seit dem Auftrag „Sendungsabruf
  // beim App-Start entfernen" (2026-08-22) ohnehin KEIN Sendungsfeld mehr - dieser lokale Zustand
  // war also nie eine Abkuerzung an einem Store-Feld vorbei, sondern IST der einzige Ort, an dem
  // eine aufgeloeste Sendung im Frontend ueberhaupt steht. Naeher an
  // `jtlLager` oben als an `TasksTab`s Zustaendigkeitsmodulen: beide laden bei EINEM Ereignis dieser
  // Komponente (dort Anmeldung, hier "eine Bestellung ist offen") ueber `useEffect` + Abbruchsignal
  // in einen lokalen `useState` - der Unterschied ist nur, DASS hier je OFFENER Bestellung neu
  // geladen wird, nicht einmal fuer die ganze Sitzung.

  /**
   * Sendung UND Zeitstrahl der offenen Bestellung, GEMEINSAM mit der Bestellkennung, zu der sie
   * gehoeren (Korrektur K-1, Pruefung von d74a35c/726b984). Vorher standen `sendung`/`zeitstrahl` in
   * ZWEI GETRENNTEN `useState` OHNE Bezug zur Bestellung, der sie zuzuordnen sind - der Reset auf
   * `null` stand nur im `!drawer`-Zweig des `useEffect` unten (Schliessen des Drawers), NICHT beim
   * Wechsel von einer offenen Bestellung auf eine andere: wer aus Bestellung A (mit verknuepfter
   * Sendung) direkt auf B (ohne) wechselte, sah bis zum Eintreffen der Antwort fuer B die Sendung
   * VON A - samt scharfem Knopf "Verknuepfung loesen", der dann die FALSCHE Bestellung getroffen
   * haette (`ShipTab/DsvSendung.tsx`) -, und `ShipTab/Transport.tsx` sperrte B's ETD/ETA/Container
   * als "DSV-gefuehrt", obwohl B gar keine Sendung hat. Schlug der Abruf fuer B fehl, blieb dieser
   * Zustand DAUERHAFT stehen: der `catch` unten meldete nur, setzte aber nichts zurueck.
   *
   * Die Kennung IM Zustand macht den Fall "falsche Bestellung" STRUKTURELL unmoeglich statt ihn durch
   * einen weiteren Reset abzufangen, den jemand spaeter vergisst: `sendung`/`zeitstrahl` unten sind
   * reine ABLEITUNGEN, die nur dann etwas liefern, wenn `speditionZustand.bestellungId` zur OFFENEN
   * Bestellung passt - unabhaengig davon, OB und WANN der `useEffect` unten ueberhaupt etwas
   * zuruecksetzt, und unabhaengig davon, ob der Abruf gelingt oder scheitert.
   */
  interface SpeditionZustand {
    bestellungId: string;
    sendung: SendungsTreffer | null;
    zeitstrahl: Zeitstrahl | null;
    /**
     * Der Zuordnungsvorschlag (Auftrag 2b, 25.08.2026) - dieselbe Bauart wie `sendung`/`zeitstrahl`:
     * eine reine ABLEITUNG unten (`vorschlag`), die nur dann etwas liefert, wenn die Kennung zur
     * OFFENEN Bestellung passt. `DsvSendung.tsx` zeigt ihn nur, solange `sendung === null`.
     */
    vorschlag: SendungsVorschlag | null;
    /**
     * Die ungefilterte DSV-Historie (Abschnitt „DSV-Historie", 2026-09-04) - VIERTER, ebenso
     * unabhaengiger Ladezweig, dieselbe Bauart wie `sendung`/`zeitstrahl`/`vorschlag`. `DsvHistorie.tsx`
     * zeigt sie nur, solange `sendung !== null` (SpeditionTab.tsx).
     */
    historie: DsvEreignis[] | null;
    /**
     * Die DSV-Belege (Zustellbeleg + Rechnung, Auftrag "DSV-Belege (Zustellbeleg + Rechnung) in der
     * Bestellung anzeigen", 04.09.2026) - FUENFTER, ebenso unabhaengiger Ladezweig, dieselbe Bauart
     * wie `sendung`/`zeitstrahl`/`vorschlag`/`historie`. ANDERS als `historie`: `DsvBelege.tsx` wird
     * NICHT an `sendung !== null` gebunden gezeigt (siehe `SpeditionTab.tsx`) - ein Beleg gehoert zur
     * BESTELLUNG, nicht zur Drawer-Anzeige der aktuell verknuepften Sendung.
     *
     * KORRIGIERT (Pruefer-Befund, KRITISCH, Nachbesserung 05.09.2026): `order_id` ist SEIT `ac18c78b`
     * ("Invoice-PDF-Download und Bestellzuordnung nachziehen/loesen", Basis-Commit dieses Auftrags)
     * KEINE einmalige Aufloesung beim Webhook-Empfang mehr, die danach unveraendert bleibt - genau
     * DIESER Commit hat `backend/src/dsv/belegVerknuepfung.ts` eingefuehrt:
     * `belegeBeimVerknuepfenNachziehen()`/`belegeBeimLoesenNachziehen()` bewegen `order_id` LIVE, sobald
     * eine Sendung ver-/entknuepft wird. `belege` wird deshalb, genau wie `zeitstrahl`, nach JEDEM
     * Verknuepfen/Loesen neu geladen (siehe `speditionVerknuepfen()`/`speditionLoesen()` unten) - die
     * fruehere Annahme "bleibt nach dem Loesen unveraendert bestehen" war falsch (siehe CHANGELOG.md).
     */
    belege: DsvBeleg[] | null;
  }
  const [speditionZustand, setSpeditionZustand] = useState<SpeditionZustand | null>(null);
  const speditionGehoertZumDrawer = speditionZustand !== null && speditionZustand.bestellungId === drawer?.id;
  const sendung = speditionGehoertZumDrawer ? speditionZustand.sendung : null;
  const zeitstrahl = speditionGehoertZumDrawer ? speditionZustand.zeitstrahl : null;
  const vorschlag = speditionGehoertZumDrawer ? speditionZustand.vorschlag : null;
  const historie = speditionGehoertZumDrawer ? speditionZustand.historie : null;
  const belege = speditionGehoertZumDrawer ? speditionZustand.belege : null;

  /**
   * Schreibt EINEN Teilaspekt (Sendung, Zeitstrahl, Vorschlag, Historie ODER Belege) fuer
   * `bestellungId` - unabhaengig von den jeweils anderen (Nebenbefund zu K-1: der bisherige
   * `Promise.all` unten war alles-oder-nichts - fiel NUR `zeitstrahlLaden` um, blieb auch die
   * bereits erfolgreich geladene Sendung ungesetzt; `historie`/`belege` sind seit dem Abschnitt
   * „DSV-Historie"/„DSV-Belege" (2026-09-04) VIERTER und FUENFTER, ebenso unabhaengiger Teilaspekt).
   * Gehoert der bisherige Zustand noch zu DERSELBEN `bestellungId`, bleibt der jeweils andere Teil
   * stehen; gehoert er zu einer ANDEREN (ein Wechsel liegt dazwischen), startet der Datensatz frisch,
   * statt zwei Bestellungen zu einem Satz zu vermischen.
   */
  function speditionTeilSetzen(
    bestellungId: string,
    teil: Partial<Pick<SpeditionZustand, "sendung" | "zeitstrahl" | "vorschlag" | "historie" | "belege">>,
  ): void {
    setSpeditionZustand((bisher) => ({
      ...(bisher?.bestellungId === bestellungId ? bisher : {
        sendung: null, zeitstrahl: null, vorschlag: null, historie: null, belege: null,
      }),
      bestellungId,
      ...teil,
    }));
  }

  /**
   * Sperrt den Verknuepfungsbereich waehrend ein Verknuepfen/Loesen-Rundlauf laeuft - MIT
   * Bestellkennung, dieselbe Bauart wie `speditionZustand` oben (G4, Auflagen-Pruefung): vorher stand
   * hier ein blosses `useState(false)` OHNE Kennung. "Verknuepfung loesen" in Bestellung A geklickt,
   * sofort Zeile B angeklickt, WAEHREND A's Rundlauf noch lief, sperrte in B das Suchfeld
   * (`DsvSendung.tsx:66`), bis A's Rundlauf zurueck war - obwohl B gar keinen eigenen Rundlauf hat.
   * Die Aussage "strukturell ausgeschlossen" (Kopfkommentar oben) galt bisher nur fuer
   * `sendung`/`zeitstrahl`. `speditionLaeuft` unten ist deshalb, wie diese beiden, eine reine
   * ABLEITUNG: `true` nur, wenn die Kennung im Zustand zur OFFENEN Bestellung passt.
   */
  interface SpeditionLaeuftZustand {
    bestellungId: string;
  }
  const [speditionLaeuftZustand, setSpeditionLaeuftZustand] =
    useState<SpeditionLaeuftZustand | null>(null);
  const speditionLaeuft =
    speditionLaeuftZustand !== null && speditionLaeuftZustand.bestellungId === drawer?.id;

  /**
   * Haelt die JEWEILS AKTUELLE `drawer`-Kennung bereit (dasselbe Muster wie `nutzerBeiRenderZeit`
   * oben) - der Race-Schutz NEBEN dem `AbortSignal` unten: eine verspaetete Antwort einer VERLASSENEN
   * Bestellung darf `speditionZustand` nicht mehr ueberschreiben (K-2, Mutationsprobe siehe
   * Kopfkommentar am `useEffect` unten). Der Grund, warum das Abbruchsignal allein nicht genuegt: in
   * Tests (und bei einer Attrappe ohne echten `fetch`) loest ein `AbortController.abort()` kein
   * Verwerfen der bereits gestellten Antwort aus - erst dieser Vergleich verhindert es. Anders als die
   * Kennung IM `speditionZustand` (die die ANZEIGE strukturell schuetzt, siehe Kopfkommentar oben)
   * verhindert dieser Riegel schon das SCHREIBEN: ohne ihn wuerde eine verspaetete Antwort einer
   * verlassenen Bestellung `speditionZustand` auf ihre EIGENE (laengst verlassene) Kennung
   * zuruecksetzen und dabei den bereits fuer die AKTUELLE Bestellung geladenen Teil mit sich reissen
   * (`speditionTeilSetzen` oben kennt immer nur EINE Kennung gleichzeitig) - unsichtbar zwar (die
   * Anzeige bleibt durch die Kennungspruefung oben korrekt leer), aber die AKTUELLE Bestellung
   * verloere ihren bereits geladenen Stand, ohne dass je wieder nachgeladen wuerde. `api/ereignisse.ts`
   * und `Shipments/SendungsSuche.tsx` machen im Baum beides bereits vor.
   */
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;
  // Geladen wird, SOBALD eine Bestellung im Drawer offen ist - nicht erst beim Klick auf den Reiter
  // "Spedition": der Reiter "Bestellung" traegt die DSV-Sperre seit dem Feldumzug nicht mehr, ein
  // Nachladen erst beim Reiterwechsel liesse die Anzeige beim ersten Blick leer.
  //
  // K-2 (Pruefung von d74a35c/726b984), Mutationsprobe: nimm den `drawerRef.current?.id !==
  // bestellungId`-Riegel im SENDUNGS-Ast (direkt unten) versuchsweise heraus - der Race-Fall in
  // `verdrahtungDrawerReiter.test.tsx` (Wechsel A->B waehrend die Antwort zu A noch aussteht) wird
  // dann rot, weil die verspaetete Antwort `speditionZustand` auf die verlassene Kennung zuruecksetzt
  // und dabei den fuer B laengst geladenen Teil mitloescht (siehe Kopfkommentar an `drawerRef`).
  //
  // G3b (Auflagen-Pruefung): NICHT ebenso pruefbar ist der GLEICHLAUTENDE Riegel im ZEITSTRAHL-Ast
  // (weiter unten) - der Race-Fall kontrolliert absichtlich nur `sendungZuBestellungLaden` von Hand
  // (eigene Resolver je Bestellung); `zeitstrahlLaden` bleibt dort `mockResolvedValue` und loest fuer
  // BEIDE Bestellungen sofort auf (siehe Kommentar dort, "wird nicht kontrolliert"). Entfernt man
  // NUR den Riegel im Zeitstrahl-Ast, bleibt der Fall trotzdem gruen: er sichert ausschliesslich die
  // SENDUNGS-Anzeige zu, und selbst wenn er den Zeitstrahl mitpruefte, waere der zuletzt geschriebene
  // Wert ohnehin der der zweiten Bestellung (o2), weil deren Aufruf schlicht spaeter erfolgt - eine
  // still bestandene Probe, keine belegte.
  useEffect(() => {
    if (!drawer) return;
    const bestellungId = drawer.id;
    const abbruch = new AbortController();

    // Nebenbefund zu K-1: Sendung und Zeitstrahl werden seither UNABHAENGIG voneinander geladen (kein
    // `Promise.all` mehr) - faellt nur einer der beiden Abrufe um, bleibt der andere trotzdem
    // sichtbar, statt dass ein einzelner Fehlschlag beide leer laesst.
    //
    // G6 (Auflagen-Pruefung) hatte fuer den Fall "BEIDE fallen" eine GEMEINSAME Meldung mit zwei Flags
    // erzwungen, weil der Toast nur einen Platz hat und der zweite `.catch` die Meldung des ersten
    // ueberschrieben haette. Der Fehlerstapel hat Platz fuer beide (Review Task 6, G-2): jeder `.catch`
    // meldet SEINE Meldung mit SEINER Ursache, ohne Wissen ueber den anderen Abruf - kein Sammelsatz, keine
    // Flags, keine Abhaengigkeit von der Antwortreihenfolge des Servers.
    sendungZuBestellungLaden(bestellungId, abbruch.signal)
      .then((geladeneSendung) => {
        if (drawerRef.current?.id !== bestellungId) return;
        speditionTeilSetzen(bestellungId, { sendung: geladeneSendung });
      })
      .catch((fehler: unknown) => {
        if (abbruch.signal.aborted) return; // eigener Abbruch (Bestellwechsel/Unmount), kein Fehler.
        if (drawerRef.current?.id !== bestellungId) return;
        console.error(`Sendung zu "${bestellungId}" konnte nicht geladen werden:`, fehler);
        meldeFehler(fehler, "Die Sendung konnte nicht geladen werden.");
      });

    zeitstrahlLaden(bestellungId, abbruch.signal)
      .then((geladenerZeitstrahl) => {
        if (drawerRef.current?.id !== bestellungId) return;
        speditionTeilSetzen(bestellungId, { zeitstrahl: geladenerZeitstrahl });
      })
      .catch((fehler: unknown) => {
        if (abbruch.signal.aborted) return;
        if (drawerRef.current?.id !== bestellungId) return;
        console.error(`Zeitstrahl zu "${bestellungId}" konnte nicht geladen werden:`, fehler);
        meldeFehler(fehler, "Der Zeitstrahl konnte nicht geladen werden.");
      });

    // Auftrag 2b (Zuordnungsvorschlag mit "Uebernehmen", 25.08.2026): UNABHAENGIG von Sendung/
    // Zeitstrahl geladen, aus demselben Grund (ein Fehlschlag hier soll die beiden anderen nicht
    // mitreissen). ANDERS als dort: der Endpunkt darf den Reiter nicht verlangsamen und sein
    // Fehlschlag ist ein STILLER Normalfall (Auftrag, wortgleich) - kein `zeigeMeldung`, nur ein
    // `console.error` fuers Debugging. Eine leere Antwort (kein Vorschlag) ist ohnehin kein Fehler,
    // sondern der `.then`-Zweig mit `null`.
    sendungsVorschlagLaden(bestellungId, abbruch.signal)
      .then((geladenerVorschlag) => {
        if (drawerRef.current?.id !== bestellungId) return;
        speditionTeilSetzen(bestellungId, { vorschlag: geladenerVorschlag });
      })
      .catch((fehler: unknown) => { // still: Zuordnungsvorschlag ist optionales Beiwerk, sein Fehlschlag ein stiller Normalfall (Auftrag 2b)
        if (abbruch.signal.aborted) return;
        console.error(`Zuordnungsvorschlag zu "${bestellungId}" konnte nicht geladen werden:`, fehler);
      });

    // Abschnitt "DSV-Historie" (2026-09-04): VIERTER, ebenso unabhaengiger Ladezweig - dasselbe
    // Muster wie `sendungsVorschlagLaden` direkt darueber. Die ungefilterte Ereignisliste ist
    // Beiwerk des Reiters (Abschnitt startet zugeklappt), ihr Fehlschlag ist deshalb ein STILLER
    // Normalfall wie dort: kein `zeigeMeldung`, nur ein `console.error` fuers Debugging - ein
    // Fehlschlag hier darf `sendung`/`zeitstrahl`/`vorschlag` nicht mit sich reissen.
    dsvHistorieLaden(bestellungId, abbruch.signal)
      .then((geladeneHistorie) => {
        if (drawerRef.current?.id !== bestellungId) return;
        speditionTeilSetzen(bestellungId, { historie: geladeneHistorie });
      })
      .catch((fehler: unknown) => { // still: DSV-Historie ist Beiwerk des Reiters (startet zugeklappt), ihr Fehlschlag soll nichts mitreissen
        if (abbruch.signal.aborted) return;
        console.error(`DSV-Historie zu "${bestellungId}" konnte nicht geladen werden:`, fehler);
      });

    // Auftrag "DSV-Belege (Zustellbeleg + Rechnung) in der Bestellung anzeigen" (04.09.2026):
    // FUENFTER, ebenso unabhaengiger Ladezweig - dasselbe Muster wie `dsvHistorieLaden` direkt
    // darueber (STILLER Normalfall bei Fehlschlag, kein `zeigeMeldung`). Gebraucht an ZWEI Stellen
    // (Reiter "Spedition" UND Reiter "Dokumente", `SpeditionTab.tsx`/`DokumenteTab.tsx`) - EIN
    // gemeinsamer Ladevorgang statt zweier unabhaengiger Abrufe fuer dieselbe Bestellung.
    dsvBelegeLaden(bestellungId, abbruch.signal)
      .then((geladeneBelege) => {
        if (drawerRef.current?.id !== bestellungId) return;
        speditionTeilSetzen(bestellungId, { belege: geladeneBelege });
      })
      .catch((fehler: unknown) => { // still: DSV-Belege: Fehlschlag laut Auftrag 04.09.2026 ein stiller Normalfall, Sendung und Zeitstrahl bleiben unberuehrt
        if (abbruch.signal.aborted) return;
        console.error(`DSV-Belege zu "${bestellungId}" konnten nicht geladen werden:`, fehler);
      });

    return () => abbruch.abort();
  }, [drawer?.id]);

  /**
   * Die Einbuchungen der offenen Bestellung, GEMEINSAM mit der Bestellkennung, zu der sie gehoeren -
   * dieselbe Bauart wie `speditionZustand` oben (K-1): ein Bestellwechsel darf nie den Datensatz der
   * VERLASSENEN Bestellung anzeigen, waehrend die Antwort fuer die neue noch aussteht.
   *
   * `fehlgeschlagen` (Fix Stufe 2, Important #2): OHNE dieses Feld liess sich "der Abruf laeuft noch
   * / hat nie begonnen" nicht von "der Abruf ist endgueltig gescheitert" unterscheiden - beide
   * Zustaende trugen `einbuchungen: null`, und `Anlieferungsliste` zeigte fuer beide denselben Satz
   * ("Einbuchungen noch nicht geladen."), obwohl die Design-Spec fuer den Fehlerfall ausdruecklich
   * "Einbuchungen konnten nicht geladen werden." verlangt. Die Meldung unten verschwand (damals als Toast, 2200 ms)
   * und der Reiter blieb danach bei der falschen Aussage stehen.
   */
  interface AnlieferungZustand {
    bestellungId: string;
    einbuchungen: EinbuchungenJePosition[] | null;
    fehlgeschlagen: boolean;
  }
  const [anlieferungZustand, setAnlieferungZustand] = useState<AnlieferungZustand | null>(null);
  const einbuchungen =
    anlieferungZustand && anlieferungZustand.bestellungId === drawer?.id
      ? anlieferungZustand.einbuchungen
      : null;
  const einbuchungenFehlgeschlagen =
    anlieferungZustand !== null &&
    anlieferungZustand.bestellungId === drawer?.id &&
    anlieferungZustand.fehlgeschlagen;

  /**
   * Die numerische JTL-Kennung der offenen Bestellung, ALS EIGENER primitiver Wert (Fix Stufe 2,
   * Important #4) - vorher hing der `useEffect` unten an `[drawer?.id, bestellungen]`, dem
   * GESAMTEN Bestellungs-Array. `bestellungen` wird bei JEDEM Bestell-Ereignis neu gebaut
   * (`state/reduzierer.ts#listeErsetzen`) - eigene Aenderung ODER ein SSE-Ereignis zu IRGENDEINER
   * Bestellung, nicht nur der offenen. Mit dem ganzen Array in den Dependencies lief der Effekt bei
   * jedem dieser Ereignisse erneut, brach den laufenden Abruf ab (`abbruch.abort()`) und stiess ihn
   * neu an - unnoetige JTL-Abfragen bei jedem Boardwechsel woanders. Der Speditions-`useEffect` oben
   * haengt bewusst NUR an `[drawer?.id]`; `jtlOrderId` ist hier das Gegenstueck: ein primitiver Wert,
   * der sich NUR aendert, wenn die JTL-Nummer der OFFENEN Bestellung selbst wechselt.
   */
  const jtlOrderId = useMemo(() => {
    const bestellung = bestellungen.find((b) => b.id === drawer?.id);
    return Number((bestellung as { jtlOrderId?: unknown } | undefined)?.jtlOrderId);
  }, [drawer?.id, bestellungen]);

  // Geladen wird, SOBALD eine Bestellung im Drawer offen ist - wie beim Reiter "Spedition" (siehe
  // dortigen Kommentar), damit die Anzeige beim ersten Blick auf den Reiter nicht leer ist.
  //
  // Abweichung von der Vorlage des Task-Briefs: dort stand `[drawer, bestellungen]` (das
  // ganze `drawer`-Objekt, nicht nur seine `id`) - GEMESSEN rot (`verdrahtungDrawerReiter.test.tsx`,
  // "Scheitert der Einbuchungs-Abruf..."): `onTab` (`setDrawer({ id: drawer.id, tab })`) baut bei
  // JEDEM Reiterwechsel ein NEUES `drawer`-Objekt, mit `drawer` in den Dependencies liesse das den
  // `useEffect` bei jedem Klick auf "Anlieferung" ERNEUT laufen - der laufende Abruf wird dabei
  // abgebrochen (`abbruch.abort()` der vorherigen Ausfuehrung) und `mockRejectedValueOnce` bereits am
  // ERSTEN (durch `drawerOeffnen()` ausgeloesten) Aufruf verbraucht, sodass der ZWEITE, durch den
  // Reiterklick ausgeloeste Aufruf auf dem haengenden Standard-Mock landet und nie auflöst. Der
  // Speditions-`useEffect` oben umgeht genau das mit `[drawer?.id]` (siehe dortige Ausfuehrung,
  // "nicht erst beim Klick auf den Reiter") - derselbe Riegel gilt hier. `jtlOrderId` (oben, Important
  // #4) ersetzt jetzt `bestellungen` selbst in den Dependencies.
  useEffect(() => {
    if (!drawer) return;
    const bestellungId = drawer.id;
    // Eine Bestellung ohne (gueltige) JTL-Nummer hat keine JTL-Positionen und damit auch keine
    // Einbuchungen - der Reiter zeigt dann ohnehin "Keine Positionen vorhanden." (Anlieferungsliste).
    if (!Number.isSafeInteger(jtlOrderId) || jtlOrderId < 1) return;
    const abbruch = new AbortController();

    einbuchungenLaden(jtlOrderId, abbruch.signal)
      .then((geladeneEinbuchungen) => {
        if (drawerRef.current?.id !== bestellungId) return;
        setAnlieferungZustand({ bestellungId, einbuchungen: geladeneEinbuchungen, fehlgeschlagen: false });
      })
      .catch((fehler: unknown) => {
        if (abbruch.signal.aborted) return;
        if (drawerRef.current?.id !== bestellungId) return;
        console.error(`Einbuchungen zu "${bestellungId}" konnten nicht geladen werden:`, fehler);
        setAnlieferungZustand({ bestellungId, einbuchungen: null, fehlgeschlagen: true });
        meldeFehler(fehler, "Die Einbuchungen konnten nicht geladen werden.");
      });

    return () => abbruch.abort();
  }, [drawer?.id, jtlOrderId]);

  /**
   * Stabile Funktionsreferenz fuer `SpeditionTabProps.sendungsSuche` - `SendungsSuche.tsx` baut
   * seine Drosselung (`jtlSuche`) mit `useMemo` an GENAU dieser Referenz auf (`gedrosselteSuche =
   * useMemo(() => jtlSuche({ abruf: suche }), [suche])`); ein neues `(begriff) => sendungenSuchen(begriff)`
   * bei jedem Tastendruck wuerde Cache und Single-Flight-Schutz bei jedem Zeichen neu aufbauen und
   * damit wirkungslos machen.
   */
  const speditionsSuche = useCallback(
    (begriff: string) => sendungenSuchen(begriff),
    [],
  );

  /**
   * R3 (Fehleranzeige Paket 1, Bestellbezug Paket 2): ERFOLGE einer nicht mehr offenen Bestellung duerfen verworfen
   * werden (die Guards `drawerRef.current?.id !== bestellungId` oben und in den Rundlaeufen), FEHLER nie. Ist der Drawer
   * weitergezogen oder geschlossen, sieht der Nutzer die Bestellung nicht mehr; dann nennt der Kontext sie.
   *
   * Dasselbe Muster wie `useDrawerTasks` (`aktuellerVorgang`/`kontextFuer`): ein A -> B -> A-Wechsel invalidiert den
   * alten A-Vorgang ebenfalls, und `bestellungenRef` haelt auch nach dem Unmount den letzten Stand der Bestellungen.
   * Die Rundlaeufe unten vergleichen weiter ueber `drawerRef` (sie schreiben nur Zustand der OFFENEN Bestellung).
   */
  const bestellungenRef = useRef(bestellungen);
  bestellungenRef.current = bestellungen;
  const istEingehaengt = useMontiertRef("passiv");
  const drawerKontext = useRef({ id: drawer?.id ?? null, folge: 0 });
  if (drawerKontext.current.id !== (drawer?.id ?? null)) {
    drawerKontext.current = { id: drawer?.id ?? null, folge: drawerKontext.current.folge + 1 };
  }

  function aktuellerVorgang(): () => boolean {
    const folge = drawerKontext.current.folge;
    return () => istEingehaengt.current && drawerKontext.current.folge === folge;
  }

  function kontextFuer(id: string, satz: string, istAktuell: () => boolean): string {
    if (istAktuell()) return satz;
    return mitBestellbezug(satz, bestellungenRef.current.find((b) => b.id === id), id);
  }

  /**
   * Nachlader fuer die zwei Rundlaeufe unten - derselbe Nachladeweg wie ueberall in dieser Datei
   * (`bestellungLaden` + `versende({ art: "nachgeladen", ... })`, siehe `aufgabenNachladen` weiter
   * unten), kein zweiter Mechanismus. NICHT optional: `sendungVerknuepfen` schreibt serverseitig
   * `etd`/`eta`/`container_type`/`container_volume` in `order_extras` und erhoeht `row_version`
   * (`backend/src/dsv/verknuepfung.ts`) - ohne dieses Nachladen zeigte der Drawer die alten Werte,
   * und die naechste Aenderung liefe in einen 409.
   */
  function speditionBestellungNachladen(bestellungId: string): Promise<void> {
    return bestellungLaden(bestellungId).then((neu) => {
      if (neu === null) return;
      versende({ art: "nachgeladen", entitaet: BESTELLUNG_ENTITAET, id: bestellungId, datensatz: neu });
    });
  }

  /**
   * `onVerknuepfen(shipmentId)` des Reiters "Spedition": verknuepft, laedt DANACH die Bestellung neu
   * (Pflicht, siehe Kopfkommentar an `speditionBestellungNachladen`), liest Sendung und Zeitstrahl
   * neu ein und zeigt server-seitig ermittelte Abweichungen - die Felder, in denen Bestellung und
   * Sendung vorher auseinanderliefen, und der EINZIGE Hinweis darauf, dass gerade von Hand gepflegte
   * Werte ueberschrieben wurden (`Abweichung.text` traegt bereits den fertigen Satz, siehe
   * `backend/src/dsv/verknuepfung.ts`).
   *
   * `umhaengen` (Sammelsendung, Spec 2026-09-07, internes zweites Argument - `DsvSendung.tsx` ruft
   * IMMER nur mit einem Argument auf, siehe dessen Kopfkommentar): eine Bestellung haengt seit
   * Migration 039 an hoechstens einer Sendung. Haengt sie bereits an einer ANDEREN, antwortet die
   * Route `409` mit `{ bisherigeSendung }` - `anfrage()` wirft das als `KonfliktFehler`
   * (`api/basis.ts`). Der `.catch` unten faengt GENAU diesen Fall VOR der allgemeinen Fehlermeldung
   * (Fehlerstapel) ab, fragt per `confirm()` nach (dasselbe Muster wie `kurssicherungLoeschenAufruf`/
   * `bestellungArchivieren` weiter unten in dieser Datei) und ruft sich bei Zustimmung MIT
   * `umhaengen: true` selbst erneut auf - ein zweiter, eigener Rundlauf, der die Sperre erneut
   * selbst setzt (`setSpeditionLaeuftZustand({ bestellungId })` unten, dieselbe `bestellungId`) und
   * ueber seine eigene `.finally` wieder abraeumt.
   *
   * `neuerVersuchGestartet` (Fixrunde 1, task-2-fix-1.md Punkt 2): OHNE dieses Flag raeumte das
   * `.finally` DIESES (ersten, gescheiterten) Rundlaufs die Sperre sofort wieder ab, NACHDEM der
   * Rekursionsaufruf sie (fuer denselben `bestellungId`) bereits neu gesetzt hatte - Promise-Ketten
   * laufen synchron innerhalb desselben Tasks weiter, das `.finally` unten sieht also schon den
   * NEUEN Lock-Wert und seine Bedingung `bisher?.bestellungId === bestellungId` trifft faelschlich
   * zu. Waehrend des gesamten Umhaenge-Rundlaufs war `laeuft` dadurch `false`, das Suchfeld
   * (`disabled={!!laeuft}` in `DsvSendung.tsx`) blieb bedienbar, ein dritter POST war ausloesbar.
   * Das Flag laesst den ERSTEN Rundlauf sein eigenes Aufraeumen ueberspringen, sobald er einen
   * zweiten angestossen hat - der zweite raeumt am Ende fuer beide auf.
   *
   * `vorgang` (Fix-Runde 1 zu Task 3, internes drittes Argument, nur fuer den Selbstaufruf unten): der "ist der Drawer noch
   * derselbe"-Pruefer des ERSTEN Versuchs. Der Selbstaufruf laeuft aus der alten Closure (`drawer` = Bestellung A) und
   * haette mit einem neuen `aktuellerVorgang()` die Folge des Drawers VON JETZT bekommen: Wechselt der Nutzer waehrend der
   * Anfrage auf B und stimmt dann dem Umhaengen zu, galt der zweite Versuch als "aktuell", und seine Meldungen (Fehler,
   * Hinweis, Nachlade-Satz) erschienen ohne Bestellbezug, obwohl B offen ist. Beide Versuche sind EIN Vorgang des Nutzers.
   */
  function speditionVerknuepfen(shipmentId: string, umhaengen?: boolean, vorgang?: () => boolean): void {
    if (!drawer) return;
    const bestellungId = drawer.id;
    const istAktuell = vorgang ?? aktuellerVorgang();
    let neuerVersuchGestartet = false;
    setSpeditionLaeuftZustand({ bestellungId });
    // NUR bei `true` als drittes Argument gereicht (wie zuvor bei `bestehendeLoesen`) - sonst haette
    // `verdrahtungDrawerReiter.test.tsx` (`expect(sendungVerknuepfen).toHaveBeenCalledWith("o1",
    // "sh1")`, GENAU zwei Argumente) ein drittes `undefined`-Argument gesehen, das dort nicht hinsoll
    // (siehe Kopfkommentar `api/sendungen.ts#sendungVerknuepfen`).
    const verknuepfenPromise = umhaengen
      ? sendungVerknuepfen(bestellungId, shipmentId, umhaengen)
      : sendungVerknuepfen(bestellungId, shipmentId);
    verknuepfenPromise
      .then(({ abweichungen, jtlErgebnisse }) => {
        // Beitritt in eine Sammelsendung mit zentraler Rechnungsnummer (Fixrunde 4): lehnt JTL das Durchschreiben ab,
        // bleibt der Beitritt bestehen, der Nutzer erfaehrt es aber (und kann es im Sammelsendungs-Drawer beheben).
        const jtlAblehnung = jtlErgebnisse?.find((e) => e.jtl === "abgelehnt");
        const jtlSatz = jtlAblehnung
          ? `In JTL nicht übernommen: Rechnungsnummer der Sammelsendung (${jtlAblehnung.fehler ?? "ohne Begründung"}). ` +
            "Im Sammelsendungs-Drawer lässt sie sich an alle Bestellungen übertragen."
          : null;
        // G1 (Auflagen-Pruefung): VOR dem Nachladen berechnet, damit die Saetze auch dann noch
        // verfuegbar sind, wenn das Nachladen weiter unten scheitert - siehe Kommentar am `.catch`.
        const saetze = (abweichungen as Array<{ text?: unknown }>)
          .map((a) => (typeof a.text === "string" ? a.text : null))
          .filter((text): text is string => text !== null);
        // Der Hinweis nach dem Verknuepfen (Fehleranzeige Paket 1, R-T6d): steht IMMER im Fehlerstapel, auch wenn er nur
        // nennt, was von DSV uebernommen wurde. Er ist die einzige Auskunft darueber, dass von Hand gepflegte Felder
        // ueberschrieben wurden; ein Toast nach 2,2 s liesse sie den Nutzer verpassen. Laeuft nach dem Nachladen UND,
        // wenn dieses scheitert, neben dessen eigener Meldung (der Stapel haelt beide). Ist der Drawer inzwischen
        // weitergezogen, nennt der Hinweis die Bestellung (R3): sonst wuesste der Nutzer nicht, wessen Felder es betrifft.
        const hinweisMelden = (): void => {
          if (saetze.length === 0 && jtlSatz === null) return;
          const dsvSatz = saetze.length > 0 ? `Von DSV übernommen - ${saetze.join(" · ")}` : null;
          meldeFehler(kontextFuer(bestellungId, [dsvSatz, jtlSatz].filter((teil) => teil !== null).join(" "), istAktuell));
        };
        return speditionBestellungNachladen(bestellungId)
          .then(() =>
            Promise.all([
              sendungZuBestellungLaden(bestellungId),
              zeitstrahlLaden(bestellungId),
              // Nachbesserung (Pruefer-Befund zu 93f54562, Wichtig 1): OHNE dieses `.catch` reisst ein
              // Fehlschlag NUR dieses Abrufs den gesamten `Promise.all` um - der `.then`-Block darunter
              // liefe nie, `setSpeditionZustand` bliebe aus, und der Reiter zeigte "keine Sendung
              // verknuepft" trotz erfolgreichem Verknuepfen. Widerspricht dem eigenen K-1-Prinzip
              // (siehe Kommentar am unabhaengigen Ladezweig oben, ca. Zeile 1868). `null` ist dasselbe
              // "nicht geladen", das `speditionTeilSetzen` und der initiale Zustand fuer `historie`
              // sonst verwenden.
              // still: Historie ist Beiwerk (siehe Ladezweig oben); null heisst "nicht geladen", der Rundlauf geht weiter
              dsvHistorieLaden(bestellungId).catch(() => null),
              // KRITISCHER Pruefer-Befund (Nachbesserung 05.09.2026): das Backend zieht seit `ac18c78b`
              // ("Belegzuordnung nachziehen/loesen") bereits zugestellte, bislang unverknuepfte Belege
              // GENAU BEIM VERKNUEPFEN nach (`backend/src/dsv/belegVerknuepfung.ts#
              // belegeBeimVerknuepfenNachziehen`) - ohne dieses Nachladen blieb ein soeben zugeordneter
              // Beleg unsichtbar, bis der Drawer neu geoeffnet wurde. Derselbe `.catch(() => null)` wie
              // bei `dsvHistorieLaden` daneben: ein Fehlschlag hier darf `sendung`/`zeitstrahl`/
              // `historie` nicht mit sich reissen.
              // still: Belege sind Beiwerk (siehe Ladezweig oben); null heisst "nicht geladen", der Rundlauf geht weiter
              dsvBelegeLaden(bestellungId).catch(() => null),
            ]),
          )
          .then(([geladeneSendung, geladenerZeitstrahl, geladeneHistorie, geladeneBelege]) => {
            if (drawerRef.current?.id === bestellungId) {
              setSpeditionZustand((bisher) => ({
                bestellungId,
                sendung: geladeneSendung,
                zeitstrahl: geladenerZeitstrahl,
                // Auftrag 2b: nach dem Uebernehmen ist die Bestellung verknuepft - der
                // Vorschlagsblock verschwindet ohnehin (DsvSendung.tsx zeigt ihn nur bei
                // `sendung === null`), `vorschlag: null` raeumt den Zustand zusaetzlich auf.
                vorschlag: null,
                // Abschnitt „DSV-Historie" (2026-09-04): derselbe Reload-Grund wie bei
                // `zeitstrahl` - die frische Verknuepfung hat i. d. R. neue `shipment_events`-Zeilen.
                historie: geladeneHistorie,
                // Abschnitt „DSV-Belege" (04.09.2026, KORRIGIERT 05.09.2026): frisch geladen, NICHT der
                // alte Stand (`bisher.belege`) - siehe Kopfkommentar an `SpeditionZustand.belege` fuer
                // den Root-Cause-Befund. Scheitert der Abruf, liefert `.catch(() => null)` oben `null`
                // ("nicht geladen"), dieselbe Bauart wie `historie`.
                belege: geladeneBelege,
              }));
            }
            // Eigene Listenkopie nachziehen; der Hook trennt Abruffehler von Aktions-Erfolg und Toast.
            zeitstrahlListeNeuLaden();
          })
          .then(hinweisMelden)
          // W-1 (Pruefung von d74a35c/726b984): das Nachladen/Neulesen NACH einem GELUNGENEN
          // `sendungVerknuepfen` faengt seinen eigenen Fehler jetzt GETRENNT ab - dasselbe Muster wie
          // `aufgabenNachladen` unten. Vorher lag dieser ganze Block noch VOR dem `.catch` der
          // aeusseren Kette: scheiterte `GET /api/orders/:id` oder eine der beiden Nachladeanfragen
          // NACH einem erfolgreichen POST, meldete die Oberflaeche "Die Sendung konnte nicht
          // verknuepft werden." - obwohl sie es SEHR WOHL war -, und der Drawer trug weiter die alten
          // `etd`/`eta`/`container_*` (also genau den 409 beim naechsten Feld, den das Nachladen
          // verhindern sollte).
          //
          // G1 (Auflagen-Pruefung): W-1 hat NUR die falsche Meldung behoben, nicht den Verlust der
          // `abweichungen` - die fielen bis hierher WEITERHIN ersatzlos weg, wenn genau DIESES
          // Nachladen scheiterte (derselbe Schaden wie vor W-1, nur hinter den `.then`-Block oben
          // verschoben statt behoben - der Nutzer erfuhr nie, dass sein von Hand gepflegtes Feld
          // ueberschrieben wurde). Frueher hatte der Toast nur EINEN Platz, also trug die
          // Fallback-Meldung die `saetze` mit. Seit dem Fehlerstapel steht jede Meldung in einem eigenen
          // Eintrag: `hinweisMelden` oben laeuft neben der Nachlade-Meldung, nicht in ihr.
          .catch((fehler: unknown) => {
            console.error(
              `Sendung "${shipmentId}" wurde mit Bestellung "${bestellungId}" verknuepft, das ` +
                "Nachladen schlug aber fehl:",
              fehler,
            );
            // R4: verknuepft ist verknuepft - der Eintrag sagt nur, dass die Anzeige neu geladen werden muss. Kein
            // Wiederholungsrat der Ursache dahinter (R-Z2): ein "erneut versuchen" liesse das Verknuepfen ein zweites Mal laufen.
            meldeNachladeFehler(fehler, kontextFuer(bestellungId, NACHLADEN_FEHLGESCHLAGEN_MELDUNG, istAktuell));
            hinweisMelden();
          });
      })
      .catch((fehler: unknown) => {
        // Sammelsendung (Spec 2026-09-07): ein 409 HIER heisst, die Bestellung haengt bereits an
        // einer ANDEREN Sendung (ux_order_shipments_order, Migration 039) - ausdrueckliche Rueckfrage
        // statt einer rohen Fehlermeldung. `!umhaengen` verhindert eine Endlosschleife: laeuft der
        // zweite Versuch (mit `umhaengen: true`) selbst nochmal in einen 409 (erneuter Wettlauf),
        // greift darunter die allgemeine Meldung im Fehlerstapel statt einer zweiten Rueckfrage.
        if (!umhaengen && fehler instanceof KonfliktFehler) {
          const bisherigeSendung = (
            fehler.koerper as { bisherigeSendung?: { dsvShipmentNo?: string } } | null
          )?.bisherigeSendung;
          const nummer = bisherigeSendung?.dsvShipmentNo ?? "einer anderen Sendung";
          // `confirm()` statt einer eigenen Dialog-Komponente (wie `kurssicherungLoeschenAufruf`/
          // `bestellungArchivieren` in dieser Datei) - der Rundlauf hier ist ein Randfall (der
          // Reiter zeigt das Suchfeld nur, solange `sendung === null` bereits bekannt ist; ein 409
          // heisst also einen zwischenzeitlichen Fremdeingriff, keinen Normalfall).
          if (confirm(`Diese Bestellung hängt bereits an Sendung ${nummer} – trotzdem umhängen?`)) {
            // Fixrunde 1, task-2-fix-1.md Punkt 2: das Flag VOR dem Rekursionsaufruf setzen, nicht
            // danach - speditionVerknuepfen() setzt die Sperre synchron als ersten Schritt, das
            // `.finally` unten muss diese neue Sperre schon beim naechsten Mikrotask NICHT mehr
            // abraeumen.
            neuerVersuchGestartet = true;
            speditionVerknuepfen(shipmentId, true, istAktuell);
          }
          return;
        }
        console.error(
          `Sendung "${shipmentId}" konnte nicht mit Bestellung "${bestellungId}" verknuepft werden:`,
          fehler,
        );
        meldeFehler(fehler, kontextFuer(bestellungId, "Die Sendung konnte nicht verknüpft werden.", istAktuell));
      })
      .finally(() => {
        // Fixrunde 1, task-2-fix-1.md Punkt 2: ein laufender zweiter Rundlauf (umhaengen: true)
        // raeumt SEINE EIGENE Sperre am Ende seiner eigenen Kette ab - dieser (erste, gescheiterte)
        // Rundlauf ueberspringt sein Aufraeumen komplett, sonst entfernte er die frisch gesetzte
        // Sperre des zweiten Rundlaufs sofort wieder (siehe Kopfkommentar dieser Funktion).
        if (neuerVersuchGestartet) return;
        // G4 (Auflagen-Pruefung): NUR entsperren, wenn der Zustand noch DIESER Bestellung gehoert -
        // sonst wuerde der Rundlauf einer laengst verlassenen Bestellung A einen INZWISCHEN fuer B
        // gesetzten Zustand mit sich reissen und B faelschlich entsperren, obwohl B's eigener
        // Rundlauf (falls einer laeuft) noch gar nicht zurueck ist.
        setSpeditionLaeuftZustand((bisher) => (bisher?.bestellungId === bestellungId ? null : bisher));
      });
  }

  /**
   * `onLoesen(shipmentId)` des Reiters "Spedition": loest die Verknuepfung, setzt die Sendung sofort
   * auf `null` und liest Zeitstrahl, Belege sowie Bestellung neu (Nachbesserung 05.09.2026: `belege`
   * ist seither Teil desselben Nachlade-Rundlaufs wie `zeitstrahl`, siehe Kommentar dort). Die Route
   * ist idempotent (200 auch ohne bestehende Verknuepfung, `backend/src/http/routes/
   * orderShipments.ts`) - kein Fehlerfall hier.
   */
  function speditionLoesen(shipmentId: string): void {
    if (!drawer) return;
    const bestellungId = drawer.id;
    const istAktuell = aktuellerVorgang();
    setSpeditionLaeuftZustand({ bestellungId });
    verknuepfungLoesen(bestellungId, shipmentId)
      .then(() => {
        if (drawerRef.current?.id === bestellungId) {
          // Nachbesserung (Pruefer-Befund zu 93f54562, Gering): `historie` gehoert zur geloesten
          // Sendung wie `sendung` selbst - ohne diesen Reset behielte der State die Ereignisliste der
          // geloesten Verknuepfung (heute unsichtbar, weil DsvHistorie nur bei `sendung !== null`
          // gerendert wird, aber inkonsistent).
          speditionTeilSetzen(bestellungId, { sendung: null, historie: null });
        }
        // Eigene Listenkopie nachziehen; der Hook trennt Abruffehler von Aktions-Erfolg und Toast.
        zeitstrahlListeNeuLaden();
        return Promise.all([
          speditionBestellungNachladen(bestellungId),
          zeitstrahlLaden(bestellungId),
          // KRITISCHER Pruefer-Befund (Nachbesserung 05.09.2026): das Backend zieht seit `ac18c78b`
          // ("Belegzuordnung nachziehen/loesen") die Belegzeilen dieser Sendung GENAU BEIM LOESEN nach
          // (`backend/src/dsv/belegVerknuepfung.ts#belegeBeimLoesenNachziehen` - je nach Restverknuepfung
          // entweder auf `order_id = NULL` zurueckgesetzt oder geloescht) - ohne dieses Nachladen zeigte
          // die Liste einen Beleg weiter an, dessen Download danach mit 404 scheitert. `belege` wird
          // deshalb NICHT synchron zurueckgesetzt (anders als `historie` oben: ohne Neuladen laesst sich
          // nicht sicher sagen, ob NACH dem Loesen ueberhaupt keine oder noch ueber eine ANDERE
          // verknuepfte Sendung erreichbare Belege uebrig bleiben), sondern - wie `zeitstrahl` - direkt
          // durch den frisch geladenen Stand ersetzt.
          // still: Belege sind Beiwerk (siehe Ladezweig oben); null heisst "nicht geladen", der Rundlauf geht weiter
          dsvBelegeLaden(bestellungId).catch(() => null),
        ])
          .then(([, geladenerZeitstrahl, geladeneBelege]) => {
            if (drawerRef.current?.id === bestellungId) {
              speditionTeilSetzen(bestellungId, { zeitstrahl: geladenerZeitstrahl, belege: geladeneBelege });
            }
          })
          // W-1, wie oben bei `speditionVerknuepfen`: geloest ist geloest - ein Fehler HIER darf das
          // nicht widerrufen. Vorher zeigte ein hier scheiterndes Nachladen "Die Verknüpfung konnte
          // nicht gelöst werden.", waehrend `sendung` (schon oben, VOR diesem Block, auf `null`
          // gesetzt) genau das Gegenteil zeigte - der Toast widersprach der eigenen Anzeige.
          .catch((fehler: unknown) => {
            console.error(
              `Verknuepfung zu Sendung "${shipmentId}" (Bestellung "${bestellungId}") wurde geloest, ` +
                "das Nachladen schlug aber fehl:",
              fehler,
            );
            // R4: geloest ist geloest - der Eintrag sagt nur, dass die Anzeige neu geladen werden muss (R-Z2: ohne
            // Wiederholungsrat der Ursache).
            meldeNachladeFehler(fehler, kontextFuer(bestellungId, NACHLADEN_FEHLGESCHLAGEN_MELDUNG, istAktuell));
          });
      })
      .catch((fehler: unknown) => {
        console.error(
          `Verknuepfung zu Sendung "${shipmentId}" (Bestellung "${bestellungId}") konnte nicht ` +
            "geloest werden:",
          fehler,
        );
        meldeFehler(fehler, kontextFuer(bestellungId, "Die Verknüpfung konnte nicht gelöst werden.", istAktuell));
      })
      .finally(() =>
        // G4 (Auflagen-Pruefung): NUR entsperren, wenn der Zustand noch DIESER Bestellung gehoert -
        // sonst wuerde der Rundlauf einer laengst verlassenen Bestellung A einen INZWISCHEN fuer B
        // gesetzten Zustand mit sich reissen und B faelschlich entsperren, obwohl B's eigener
        // Rundlauf (falls einer laeuft) noch gar nicht zurueck ist.
        setSpeditionLaeuftZustand((bisher) => (bisher?.bestellungId === bestellungId ? null : bisher)),
      );
  }

  /**
   * Laedt die DSV-Belege der offenen Bestellung neu - derselbe Ladeweg wie das erste Laden im Effekt oben
   * (`dsvBelegeLaden` + `speditionTeilSetzen`), nur auf Abruf: nach „Kategorie zuweisen …“ am Beleg
   * (Spec 2026-10-17) traegt die Zeile ihre `manuelleZuordnung` erst in der neu geladenen Liste. Gibt
   * die Zusage zurueck und lehnt bei einem Ladefehler ab - der Aufrufer meldet ihn (die Zuordnung selbst
   * ist dann schon gespeichert, nur die Liste ist veraltet).
   */
  function belegeNeuLaden(): Promise<void> {
    if (!drawer) return Promise.resolve();
    const bestellungId = drawer.id;
    return dsvBelegeLaden(bestellungId).then((geladeneBelege) => {
      if (drawerRef.current?.id !== bestellungId) return;
      speditionTeilSetzen(bestellungId, { belege: geladeneBelege });
    });
  }

  return {
    sendung, zeitstrahl, vorschlag, historie, belege, einbuchungen, einbuchungenFehlgeschlagen,
    speditionsSuche, speditionVerknuepfen, speditionLoesen, speditionLaeuft, belegeNeuLaden,
  };
}
